"use client";

import { Building2, Loader2, MapPin, SearchX } from "lucide-react";
import type {
  ItemSuggestion,
  PlaceSuggestion,
  SearchSuggestion,
} from "@/lib/search";

interface Props {
  activeIndex: number;
  /** Show "No results" empty state instead of results */
  emptyResults: boolean;
  id: string;
  items: ItemSuggestion[];
  /** Show a loading spinner while fetches are in-flight */
  loading: boolean;
  onSelect: (suggestion: SearchSuggestion) => void;
  places: PlaceSuggestion[];
  /** The current query string — shown in the empty state message */
  query: string;
}

export function SearchDropdown({
  activeIndex,
  emptyResults,
  id,
  items,
  loading,
  onSelect,
  places,
  query,
}: Props) {
  return (
    <div
      className="absolute top-full left-0 z-30 mt-1 max-h-80 w-full overflow-y-auto rounded-xl border border-border bg-background shadow-lg"
      id={id}
      role="listbox"
    >
      {/* Loading state */}
      {loading && (
        <div className="flex items-center gap-2.5 px-3 py-3 text-muted-foreground text-sm">
          <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" />
          <span>Searching…</span>
        </div>
      )}

      {/* Empty state — only shown after loading finishes with no results */}
      {!loading && emptyResults && (
        <div className="flex items-center gap-2.5 px-3 py-3 text-muted-foreground text-sm">
          <SearchX className="h-3.5 w-3.5 shrink-0" />
          <span>
            No results for{" "}
            <span className="font-medium text-foreground">"{query}"</span>
          </span>
        </div>
      )}

      {/* Places section */}
      {!loading && places.length > 0 && (
        <div>
          <p className="px-3 pt-2 pb-1 font-medium text-[11px] text-muted-foreground uppercase tracking-wide">
            Places
          </p>
          {places.map((s, i) => (
            <button
              aria-selected={activeIndex === i}
              className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm hover:bg-accent aria-selected:bg-accent"
              id={`search-opt-${i}`}
              key={s.id}
              onClick={() => onSelect(s)}
              role="option"
              type="button"
            >
              <MapPin className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              <span className="truncate">{s.displayName}</span>
            </button>
          ))}
        </div>
      )}

      {/* Groups & Programmes section */}
      {!loading && items.length > 0 && (
        <div>
          {places.length > 0 && (
            <div className="mx-3 my-1 border-border border-t" />
          )}
          <p className="px-3 pt-2 pb-1 font-medium text-[11px] text-muted-foreground uppercase tracking-wide">
            Groups &amp; Programmes
          </p>
          {items.map((s, i) => {
            const absIndex = places.length + i;
            return (
              <button
                aria-selected={activeIndex === absIndex}
                className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm hover:bg-accent aria-selected:bg-accent"
                id={`search-opt-${absIndex}`}
                key={s.id}
                onClick={() => onSelect(s)}
                role="option"
                type="button"
              >
                {s.kind === "programme" && s.logoUrl ? (
                  // biome-ignore lint/performance/noImgElement: small inline logo; Next.js Image adds no benefit here
                  <img
                    alt=""
                    className="h-5 w-5 shrink-0 object-contain"
                    height={20}
                    src={s.logoUrl}
                    width={20}
                  />
                ) : (
                  <Building2 className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                )}
                <span className="min-w-0 flex-1 truncate">
                  {s.name}
                  {s.subtitle && (
                    <span className="ml-1 text-muted-foreground text-xs">
                      · {s.subtitle}
                    </span>
                  )}
                </span>
                {s.city && (
                  <span className="ml-auto shrink-0 text-muted-foreground text-xs">
                    {s.city}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
